import { request } from './request';
import {
  type ConfigOption
} from './configuration'
// 账户信息
export interface AccountInfo {
  id?: number;
  email?: string;
  password?: string;
  status?: number;
  position?: string;
  name?: string;
  enterpriseId?: number;
  roleId?: number;
  /** Stream Chat 用户 id（服务端生成的不透明串），发起会话必须用它 */
  streamChatId?: string;
}

// 公司信息
export interface CompanyInfo {
  name?: string;
  employmentInsuranceNumber?: string;
  companyCategory?: number;
  mainIndustry?: number[];
  annualSales?: number;
  foreignEmployeeCount?: number;
  companyAddress?: string;
  corporateNumber?: string;
  employmentOfficeNumber?: string;
  companyType?: number;
  registeredCapital?: number;
  regularEmployeeCount?: number;
}

// 联系人信息
export interface ContactInfo {
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  contactPosition?: string;
}

// 分支机构信息
export interface BranchInfo {
  id?: number;
  name?: string;
  address?: string;
  enterpriseId?: number;
  [key: string]: unknown;
}

// 附件信息
export interface AttachmentInfo {
  id?: number;
  attachType?: string;
  fileKey?: string;
  fileName: string;
  mimeType?: string;
  size?: number;
  url: string;
  updateTime?: string;
  updateBy?: string;
  createByName?: string;
  filePath: string;
}

// 行业信息
export interface IndustryInfo {
  id?: number;
  industryId?: string;
  name?: string;
}

// 公司类型选项
export interface CompanyTypeOption {
  label?: string;
  value?: number;
}

// 公司类别和类型级联数据
export interface CompanyCategoryType {
  categoryCode?: number;
  categoryName?: string;
  types?: CompanyTypeOption[];
}

// 行业文件信息
export interface IndustryFileInfo {
  id?: number;
  industryId?: string;
  name?: string;
  fileTypes?: string | null;
}

// 企业信息接口
export interface EnterpriseInfo {
  id?: number;
  accountInfo?: AccountInfo;
  companyInfo?: CompanyInfo;
  contactInfo?: ContactInfo;
  branchInfoList?: BranchInfo[];
  attachmentList?: AttachmentInfo[];
}

// 获取企业信息
export function getEnterpriseInfoApi(id: number | string): Promise<EnterpriseInfo> {
  return request({
    url: `/rs-enterprise/api/v1/enterprise-info/${id}`,
    method: 'GET'
  });
}

// 更新企业信息
export function updateEnterpriseInfoApi(id: number | string, data: EnterpriseInfo): Promise<EnterpriseInfo> {
  return request({
    url: `/rs-enterprise/api/v1/enterprise-info/${id}`,
    method: 'PUT',
    data
  });
}

// 获取企业账号列表（ID-Auth）
export function getEnterpriseAccountListApi(enterpriseId: number | string, params?: { p?: number | string }): Promise<AccountInfo[]> {
  return request({
    url: `/id-auth/api/v1/enterprise/account-list/${enterpriseId}`,
    method: 'GET',
    params,
  });
}

/** 企业下可联系猎头公司列表 */
export function getHeadhuntingListApi(): Promise<any[]> {
  return request({
    url: '/id-enterprise/api/v1/enterprise/headhunting-list',
    method: 'GET',
  })
}

/** 猎头下可联系企业公司列表 */
export function getEnterprisListApi(): Promise<any[]> {
  return request({
    url: '/id-enterprise/api/v1/headhunting/enterprise-list',
    method: 'GET',
  })
}

export interface ContactSearchAccount {
  id: number
  name?: string
  email?: string
  position?: string
  status?: number
  enterpriseId?: number
  avatar?: string
  /** Stream Chat 用户 id（服务端生成的不透明串），发起会话必须用它 */
  streamChatId?: string
  roleInfo?: { id: number; name: string; description: string }
}

export interface ContactSearchGroup {
  enterpriseId: number
  enterpriseName: string
  accountList: ContactSearchAccount[]
}

/** 企业端：猎头通讯账号模糊搜索 */
export function searchHeadhuntingContactsApi(q: string, p?: number | string): Promise<ContactSearchGroup[]> {
  return request({
    url: '/id-enterprise/api/v1/enterprise/headhunting-search',
    method: 'GET',
    params: { q, p },
  })
}

/** 猎头端：企业通讯账号模糊搜索 */
export function searchEnterpriseContactsApi(q: string, p?: number | string): Promise<ContactSearchGroup[]> {
  return request({
    url: '/id-enterprise/api/v1/headhunting/enterprise-search',
    method: 'GET',
    params: { q, p },
  })
}

// 获取企业附件列表
export function getEnterpriseAttachmentsApi(fileKey?: string): Promise<AttachmentInfo[]> {
  return request({
    url: `/rs-enterprise/api/v1/enterprise-attachments`,
    method: 'GET',
    params: { fileKey }
  });
}

// 上传企业附件
export function uploadEnterpriseAttachmentApi(
  enterpriseId: number | string,
  attachType: string,
  file: File
): Promise<AttachmentInfo> {
  const formData = new FormData()
  formData.append('enterpriseId', String(enterpriseId))
  formData.append('fileKey', attachType)
  formData.append('file', file)
  
  return request({
    url: `/rs-enterprise/api/v1/enterprise-attachment`,
    method: 'POST',
    data: formData
  });
}

// 删除企业附件
export function deleteEnterpriseAttachmentApi(attachmentId: number | string): Promise<void> {
  return request({
    url: `/rs-enterprise/api/v1/enterprise-attachment/${attachmentId}`,
    method: 'DELETE'
  });
}

// 获取行业列表
export function getIndustriesApi(params?: { ifSkill1?: boolean | number; ifSkill2?: boolean | number, forSkill?: number }): Promise<IndustryInfo[]> {
  return request({
    url: `/rs-enterprise/api/v1/dim/industries`,
    method: 'GET',
    params
  });
}

// 获取公司类别和类型级联数据
export function getCompanyTypeApi(): Promise<CompanyTypeOption[]> {
  return request({
    url: `/rs-enterprise/api/v1/enums/EnterpriseCategoryEnum`,
    method: 'GET'
  });
}

// 根据行业ID、公司类别、公司类型获取文件列表
export function getIndustryFilesApi(
  industryId?: (number | string)[] | number | string,
  enterpriseCategory?: number | string,
): Promise<IndustryFileInfo[]> {
  return request({
    url: `/rs-enterprise/api/v1/dim/enterprise-files`,
    method: 'GET',
    params: {
      industryId,
      enterpriseCategory,
    }
  });
}

// 获取企业分支机构列表
export function getEnterpriseBranchesApi(enterpriseId?: number | string): Promise<BranchInfo[]> {
  return request({
    url: `/rs-enterprise/api/v1/enterprise-branches`,
    method: 'GET',
    params: enterpriseId ? { enterpriseId } : undefined
  });
}

// 在留资格选项
export interface ResidenceQualificationOption {
  value?: number | string;
  label?: string;
  [key: string]: unknown;
}

// 获取在留资格选项列表
export function getResidenceQualificationEnumApi(): Promise<ConfigOption[]> {
  return request({
    url: `/rs-enterprise/api/v1/enums/ResidenceQualificationEnum`,
    method: 'GET'
  });
}

// 获取资格外活动选项列表
export function getPermissionForActivitiesEnumApi(): Promise<ConfigOption[]> {
  return request({
    url: `/rs-enterprise/api/v1/enums/PermissionForActivitiesEnum`,
    method: 'GET'
  });
}

// 获取就业限制选项列表
export function getWorkRestrictionEnumApi(): Promise<ConfigOption[]> {
  return request({
    url: `/rs-enterprise/api/v1/enums/WorkRestrictionEnum`,
    method: 'GET'
  });
}
// 获取入国目的
export function getPurposeEnumEnumApi(): Promise<ConfigOption[]> {
  return request({
    url: `/rs-enterprise/api/v1/enums/PurposeEnum`,
    method: 'GET'
  });
}

// 获取企业详情（招聘企业管理）
export function getEnterpriseDetailApi(enterpriseId: number | string): Promise<Company.EnterpriseDetailResponse> {
  return request({
    url: `/id-enterprise/api/enterprise-info/detail/${enterpriseId}`,
    method: 'GET'
  });
}

// 更新企业备注
export function updateEnterpriseRemarksApi(enterpriseId: number | string, remarks: string): Promise<void> {
  return request({
    url: `/id-enterprise/api/enterprise-info/${enterpriseId}/remarks`,
    method: 'PUT',
    data: { remarks }
  });
}

// 文件 URL 类型：1-预览，2-下载
export enum FileUrlType {
  PREVIEW = 1,
  DOWNLOAD = 2
}

// 根据 S3 文件 Key 获取预览/下载地址
export function getFileUrlApi(s3Key: string, type: FileUrlType): Promise<string> {
  return request({
    url: `/id-enterprise/api/v1/file/url`,
    method: 'GET',
    params: { s3Key, type }
  });
}

// 更新企业标签
export function updateEnterpriseTagsApi(enterpriseId: number | string, tagIds: number[]): Promise<void> {
  return request({
    url: `/id-enterprise/api/enterprise-info/${enterpriseId}/tags`,
    method: 'PUT',
    data: { tagIds }
  });
}

// 导出所有类型
export type {
  AccountInfo,
  CompanyInfo,
  ContactInfo,
  BranchInfo,
  AttachmentInfo,
  IndustryInfo,
  CompanyCategoryType,
  CompanyTypeOption,
  IndustryFileInfo,
  ResidenceQualificationOption
}

